import React, { Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import "../Style/scss/pages/login.scss";
import "../Style/scss/pages/dashboard.scss";
import Loader from "../common/Loader";
import AddInvestorClient from "../pages/clientManagement/AddInvestorClient";
import ClientsAccount from "../pages/clientManagement/ClientsAccount";

// Lazy imports for better code splitting
const Signin = React.lazy(() => import("../Authentication/Signin"));
const ResetPassword = React.lazy(() => import("../Authentication/ResetPassword"));

const Layout = React.lazy(() => import("./layout"));
const ProtectedRoute = React.lazy(() => import("./ProtectedRoute"));

// Shared Components
const ChangePassword = React.lazy(() => import("../Authentication/ChangePassword"));

// Super Admin Pages
const UserList = React.lazy(() => import("../pages/userManagement/UserList"));
const AddUser = React.lazy(() => import("../pages/userManagement/AddUser"));
const EditUser = React.lazy(() => import("../pages/userManagement/EditUser"));
const Dashboard = React.lazy(() => import("../pages/dashboard/dashboard"));
const ClientList = React.lazy(() => import("../pages/clientManagement/ClientList"));
const AddClient = React.lazy(() => import("../pages/clientManagement/AddClient"));
const EditClient = React.lazy(() => import("../pages/clientManagement/EditClient"));
const ClientDetails = React.lazy(() => import("../pages/clientManagement/ClientDetails"));
const AddAdvisor = React.lazy(() => import("../pages/financialAdvisorManagement/AddAdvisor"));
const EditAdvisor = React.lazy(() => import("../pages/financialAdvisorManagement/EditAdvisor"));
const AdvisorList = React.lazy(() => import("../pages/financialAdvisorManagement/AdvisorList"));
const DocumentList = React.lazy(() => import("../pages/documents/DocumentList").then(module => ({ default: module.DocumentList })));
const DocumentDetails = React.lazy(() => import("../pages/documents/DocumentDetails"));
const AuditLogs = React.lazy(() => import("../pages/AuditLogs/AuditLogs"));
const AllRequests = React.lazy(() => import("../pages/requests/AllRequests"));

// Client Pages
const SendRequest = React.lazy(() => import("../pages/client/requests/SendRequest"));
const RelatedPartyListing = React.lazy(() => import("../pages/client/relatedParty/RelatedPartyListing"));
const RelatedPartyDetail = React.lazy(() => import("../pages/client/relatedParty/RelatedPartyDetail"));
const ShareholderAccounts = React.lazy(() => import("../pages/client/shareholderAccounts/ShareholderAccounts"));
const AccountPortfolioSummary = React.lazy(() => import("../pages/client/shareholderAccounts/AccountPortfolioSummary"));
const AccountPortfolioSecurityView = React.lazy(() => import("../pages/client/shareholderAccounts/AccountPortfolioSecurityView"));
const Profile = React.lazy(() => import("../pages/client/profile/profile"));
const AddFinancialAdvisor = React.lazy(() => import("../pages/client/advisor/AddFinancialAdvisor"));
const EditFinancialAdvisor = React.lazy(() => import("../pages/client/advisor/EditFinancialAdvisor"));
const ClientDocument = React.lazy(() => import("../pages/client/document/ClientDocument"));

// Advisor Pages
const AdvDocument = React.lazy(() => import("../pages/financialAdvisor/AdvDocument"));
const AdvClientsList = React.lazy(() => import("../pages/financialAdvisor/AdvClientsList"));
const AdvClientDetails = React.lazy(() => import("../pages/financialAdvisor/AdvClientDetails"));
const SetupAccount = React.lazy(() => import("../Authentication/SetupAccount"));
const ClientAdvisorsDocument = React.lazy(() => import("../pages/client/advisor/ClientAdvisorsDocument"));
const GenericSecurityType = React.lazy(() => import("../components/PortfolioSummary/GenericSecurityType"));
const ClientGenericSecurityType = React.lazy(() => import("../components/PortfolioSummary/ClientGenericSecurity"));
const ClientDashGenericSecurity = React.lazy(() => import("../components/PortfolioSummary/ClientDashGenericSecurity"));
const VerifyOtp = React.lazy(() => import("../Authentication/VerifyOtp"));
const UpdateResetPassword = React.lazy(() => import("../Authentication/UpdateResetPassword"));
const GenericPerformanceDetails = React.lazy(() => import("../components/PortfolioSummary/GenericPerformanceDetails"));
const ProfileTabForAdvisors = React.lazy(() => import("../pages/financialAdvisorManagement/FinancialAdvisorTabs").then(module => ({ default: module.ClientDetails })));


export default function AppRoutes() {
  const userData = JSON.parse(localStorage.getItem("user"));
  const loggedIn = !!userData?.data?.AccessToken;

  return (
    <Suspense fallback={<Loader />}>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/"
            element={
              <Navigate to={loggedIn ? "/dashboard" : "/forum-login"} replace />
            }
          />
          <Route path="/setup-account" element={<SetupAccount />} />
          <Route path="*" element={<Navigate to="/forum-login" replace />} />
          <Route
            path="/forum-login"
            element={
              loggedIn ? <Navigate to="/client-list" replace /> : <Signin />
            }
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-otp" element={<VerifyOtp />} />
          <Route path="/update-password" element={<UpdateResetPassword />} />
          {/* Shared Protected Routes */}
          <Route
            element={
              <ProtectedRoute
                allowedRoles={["super-admin", "related-party", "admin", "client", "advisor"]}
              />
            }
          >
            <Route element={<Layout />}>
              <Route path="/my-profile" element={<Profile />} />
              <Route path="/change-password" element={<ChangePassword />} />
              <Route path="/document-details" element={<DocumentDetails />} />
              <Route path="/view-security/:id" element={<GenericSecurityType />} />
              <Route path="/client-view-security/:id" element={<ClientGenericSecurityType />} />
              <Route path="/clientDash-view-security/:id" element={<ClientDashGenericSecurity />} />
              <Route path="/view-performance/:name/:ownerId" element={<GenericPerformanceDetails />} />
              <Route path="/account-portfolio/:clientId/:accountId" element={<AccountPortfolioSummary />} />
              <Route path="/account-portfolio-security/:clientId/:accountId/:id" element={<AccountPortfolioSecurityView />} />
            </Route>
          </Route>

          {/* Super Admin Only */}
          <Route element={<ProtectedRoute allowedRoles={["super-admin", "admin", "employee"]} />}>
            <Route element={<Layout />}>
              <Route path="/user-list" element={<UserList />} />
              <Route path="/add-user" element={<AddUser />} />
              <Route path="/edit-user/:id" element={<EditUser />} />
              <Route path="/advisor-details/:id" element={<ProfileTabForAdvisors />} />
              <Route path="/client-list" element={<ClientList />} />
              <Route path="/add-client" element={<AddClient />} />
              <Route path="/edit-client" element={<EditClient />} />
              <Route path="/client-details/:id" element={<ClientDetails />} />
              <Route path="/client/add-advisor" element={<AddAdvisor />} />
              <Route path="/advisor/add-investor-client/:id" element={<AddInvestorClient />} />
              <Route path="/advisor-list" element={<AdvisorList />} />
              <Route path="/add-advisor" element={<AddAdvisor />} />
              <Route path="/edit-advisor/:id" element={<EditAdvisor />} />

              <Route path="/document-list" element={<DocumentList />} />
              <Route path="/audit-list" element={<AuditLogs />} />
            </Route>
          </Route>
          <Route element={<ProtectedRoute allowedRoles={["super-admin", "admin"]} />}>
            <Route element={<Layout />}>
              <Route path="/requests" element={<AllRequests />} />
            </Route>
          </Route>
          {/* Client Only */}
          <Route element={<ProtectedRoute allowedRoles={["client", "related-party"]} />}>
            <Route element={<Layout />}>
              <Route
                path="/add-financial-advisor"
                element={<AddFinancialAdvisor />}
              />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/accounts" element={<ClientsAccount />} />
              <Route path="/edit-advisors" element={<EditFinancialAdvisor />} />
              <Route path="/client-documents" element={<ClientDocument />} />
              <Route path="/investments" element={<div className="page__body" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>Investments — coming soon</div>} />
              <Route
                path="/account-history"
                element={<div>Account History Page</div>}
              />
              <Route path="/advisors-details/:id" element={<ClientAdvisorsDocument />} />
            </Route>
          </Route>

          {/* Client Only - Send Request */}
          <Route element={<ProtectedRoute allowedRoles={["client","employee"]} />}>
            <Route element={<Layout />}>
              <Route path="/send-request" element={<SendRequest />} />
            </Route>
          </Route>

          {/* Client Only - Related Party Listing */}
          <Route element={<ProtectedRoute allowedRoles={["client"]} />}>
            <Route element={<Layout />}>
              <Route path="/related-party-listing" element={<RelatedPartyListing />} />
              <Route path="/related-party-details/:id" element={<RelatedPartyDetail />} />
              <Route path="/shareholder-accounts" element={<ShareholderAccounts />} />
            </Route>
          </Route>

          {/* Advisor Only */}
          <Route element={<ProtectedRoute allowedRoles={["advisor"]} />}>
            <Route element={<Layout />}>
              <Route path="/adv-dashboard" element={<AdvClientsList />} />
              <Route path="/advisor-document" element={<AdvDocument />} />
            </Route>
          </Route>
          {/* Shared route for client & advisor */}
          <Route
            element={<ProtectedRoute allowedRoles={["advisor", "related-party", "client"]} />}
          >
            <Route element={<Layout />}>
              <Route
                path="/adv-clients/client-details/:id"
                element={<AdvClientDetails />}
              />
            </Route>
          </Route>
        </Routes>
      </Router>
    </Suspense>
  );
}
